import React, { useEffect, useState } from "react";
import { Form, Input, InputNumber, Radio, Button, Card, Row, Col, notification } from "antd";
import { UserOutlined } from "@ant-design/icons";
import PageLayoutWrapper from "components/PageLayoutWrapper";
import { useAuth } from "services/Auth";
import MDSelect from "components/util-components/MDSelect";
import { isNil } from "@salespark/toolkit";
import { useHistory } from "react-router-dom";
import { offlineHandler } from "utils/offlineCache";
import useSelectedService from "hooks/useSelectedService";
import { locationTracker } from "utils/location/geolocation";
import { generateActionReference } from "utils/location/record";
import { LOCATION_ACTIONS } from "constants/location";

const { TextArea } = Input;
const isMissingTokenQueueError = (response) =>
  response?.status === false &&
  String(response?.data || "")
    .toLowerCase()
    .includes("staff token is required");

const Fuel = () => {
  const { auth } = useAuth();
  const apiClient = offlineHandler(auth.data);
  const { selectedService } = useSelectedService();
  const history = useHistory();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [wares, setWares] = useState([]);
  const [equipments, setEquipments] = useState([]);

  const [state, setState] = useState({
    staff: null,
    wares: null,
    equipment: null,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const waresResponse = await apiClient.get("/goods/wares");

      if (waresResponse.status === false) {
        notification.error({
          title: "Erro",
          description: "Erro ao carregar os artigos",
        });
        await apiClient.post("/error-capture", {
          error: waresResponse.data,
          action: "/Fuel/loadData/get wares",
          timestamp: new Date().toISOString(),
        });
        return;
      }

      const wares = waresResponse.data?.filter((w) => w.type === "fuel");

      setWares(wares);
      if (isNil(selectedService)) {
        const equipmentsResponse = await apiClient.get("/equipments");

        if (equipmentsResponse.status === false) {
          notification.error({
            title: "Erro",
            description: "Erro ao carregar os equipamentos",
          });
          await apiClient.post("/error-capture", {
            error: equipmentsResponse.data,
            action: "/Fuel/loadData/ get equipments",
            timestamp: new Date().toISOString(),
          });
          return;
        }
        const equipments = equipmentsResponse.data.filter((e) => e.equipment_type === "vessel" && e.hour_control === true);
        setEquipments(equipments);
      } else {
        setEquipments(selectedService.equipment);
      }

      // Error handling
    } catch (error) {
      notification.error({
        title: "Erro ao carregar dados",
        description: error,
      });
      await apiClient.post("/error-capture", {
        error: error,
        action: "/Fuel/loadData/catch",
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        equipment_1: "6806467c73a94fef34f2e024", // Cais (Armazém cais é fixo)
        type: "67e15380419c69b84d23d9aa", //transfer type id
        date: new Date().toISOString(),
      };

      const response = await apiClient.post("/goods/transfer", payload);

      if (response?.status === false) {
        if (isMissingTokenQueueError(response)) {
          notification.error({
            title: "Ação bloqueada",
            description: "Não foi possível guardar o abastecimento offline porque falta o token do staff. Faz login online e tenta novamente.",
          });
          return;
        }
        notification.error({
          title: "Erro",
          description: `Erro ao criar transferência (abastecimento)`,
        });
        await apiClient.post("/error-capture", {
          error: response.data,
          action: "/Fuel/handleSubmit/ post /goods/transfer",
          timestamp: new Date().toISOString(),
        });
      }

      if (response?.status !== false) {
        // Capture the fuel-refuel location (best-effort; never blocks the action).
        // No-op when there is no active service (nothing to attribute the position to).
        locationTracker.captureActionLocation({
          service_id_raw: selectedService?.service_id_raw || null,
          equipment_id: selectedService?.equipment?.[0]?._id || null,
          action: LOCATION_ACTIONS.FUEL_REFUEL,
          action_reference: generateActionReference(),
        });
      }

      form.resetFields();
      history.push("/home");
    } catch (error) {
      notification.error({
        title: "Erro",
        description: "Erro ao guardar transferências.",
      });
      await apiClient.post("/error-capture", {
        error: error,
        action: "/Fuel/handleSubmit/catch",
        timestamp: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayoutWrapper title="Abastecimento de Combustível">
      <Card size="small">
        <Form form={form} layout={"vertical"} onFinish={handleSubmit} requiredMark="optional" className="compact-form">
          <Form.Item label={<span style={{ fontWeight: 500 }}>Staff</span>} name="staff">
            <Input prefix={<UserOutlined />} disabled placeholder="Nome do Staff" value={auth.data._id} defaultValue={auth.data.name} />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Combustível</span>}
            name="wares"
            rules={[{ required: true, message: "Por favor selecione o tipo de combustível" }]}
          >
            <MDSelect
              form={form}
              size="large"
              name="wares"
              data={wares}
              itemId="_id"
              itemData="description"
              selectedId={state.wares}
              onSelect={(value) => setState({ ...state, wares: value })}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item label={<span style={{ fontWeight: 500 }}>De onde é abastecido</span>} name="equipment_1" tooltip="Cais (Armazém cais é fixo)">
            <Input disabled value="Armazém Cais" defaultValue={"Cais"} />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Barco abastecido</span>}
            name="equipment_2"
            rules={[{ required: true, message: "Por favor selecione o barco" }]}
          >
            <MDSelect
              form={form}
              size="large"
              name="equipment_2"
              data={equipments}
              itemId="_id"
              itemData="name"
              minItemWidth={180}
              selectedId={state.equipment?._id}
              onSelect={(value) => {
                const equipment = equipments.find((e) => e._id === value);
                setState({ ...state, equipment: equipment });
              }}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Row gutter={24}>
            <Col xs={24} sm={8}>
              <Form.Item label={<span style={{ fontWeight: 500 }}>Litros</span>} name="quantity" rules={[{ required: true, message: "Campo obrigatório" }]}>
                <InputNumber placeholder="0" min={0} style={{ width: "100%" }} precision={2} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Atestado S/N</span>}
                name="vehicle_filled"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Radio.Group style={{ width: "100%" }} buttonStyle="solid">
                  <Radio.Button value={true}>Sim</Radio.Button>
                  <Radio.Button value={false}>Não</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Horas de Motor</span>}
                name="equipment_hours"
                extra={
                  <small className="text-muted">
                    <em>Último Registo: {state.equipment?.last_recorded_hour} horas</em>
                  </small>
                }
                rules={[
                  { required: true, message: "Campo obrigatório" },
                  () => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.resolve();
                      }
                      if (isNaN(value)) {
                        return Promise.reject("Não é um número válido");
                      }

                      if (parseInt(value) === 0) {
                        return Promise.reject("Não é possível inserir 0 horas");
                      }
                      if (parseInt(value) - state.equipment?.last_recorded_hour > 1000) {
                        return Promise.reject("Não é possível inserir horas com diferença superior a 1000");
                      }

                      if (parseInt(value) < state.equipment?.last_recorded_hour || 0) {
                        return Promise.reject("Horas atuais deve ser maior que o último registo");
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber
                  placeholder="0"
                  min={0}
                  style={{ width: "100%" }}
                  parser={(value) => {
                    if (!value) return "";
                    const normalized = value.replace(/,/g, ".").replace(/[^\d.]/g, "");
                    const num = parseFloat(normalized);
                    if (isNaN(num)) return "";

                    return Math.floor(num);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={<span style={{ fontWeight: 500 }}>Observações</span>} name="notes">
            <TextArea rows={1} style={{ resize: "none" }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ textAlign: "center" }}>
              <Button
                htmlType="submit"
                loading={loading}
                size="large"
                className="shopping-button"
                style={{
                  height: 30,
                  fontSize: 16,
                  /*width: "50%",
                left: "25%",*/
                  fontWeight: 700,
                  borderRadius: 12,
                  /*boxShadow: "0 4px 12px rgba(55, 118, 104, 0.3)",*/
                }}
              >
                Registar Abastecimento
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </PageLayoutWrapper>
  );
};

export default Fuel;
